import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import Button from '../components/ui/Button';
import { 
  FaQrcode, 
  FaInfinity, 
  FaMobileAlt, 
  FaCheckCircle, 
  FaEdit,
  FaShieldAlt,
  FaChartLine,
  FaLeaf,
  FaUsers,
  FaBolt,
  FaGlobe,
  FaLock,
  FaClock,
  FaRocket,
  FaHeart,
  FaStar,
  FaLink,
  FaLayerGroup,
  FaWifi,
  FaCloud,
  FaDatabase,
  FaPalette,
  FaChartBar,
  FaUserShield
} from 'react-icons/fa';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiArrowLeft, FiMenu, FiX, FiZap, FiShield, FiTrendingUp } = FiIcons;

const Features = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const mainFeatures = [
    {
      icon: FaInfinity,
      title: "Unlimited Updates",
      description: "Change your contact info, social links, or business details anytime without reprinting cards.",
      color: "from-blue-600 to-blue-800",
      benefits: [
        "Update instantly from anywhere",
        "No reprinting costs",
        "Always current information",
        "Unlimited changes"
      ]
    },
    {
      icon: FaMobileAlt,
      title: "Instant Sharing",
      description: "Simple tap or scan to share everything. Works with any smartphone, no app installation needed.",
      color: "from-green-600 to-green-800",
      benefits: [
        "Works on all smartphones",
        "No app download required",
        "One tap to share",
        "Universal compatibility"
      ]
    },
    {
      icon: FiShield,
      title: "Secure & Private",
      description: "Your data is protected with enterprise-grade security. Only you control what you share.",
      color: "from-purple-600 to-purple-800",
      benefits: [
        "Bank-level encryption",
        "Complete data control",
        "Privacy-first design",
        "Secure cloud storage"
      ]
    },
    {
      icon: FiTrendingUp,
      title: "Analytics Included",
      description: "Track how many people scan your card and measure your networking success.",
      color: "from-yellow-600 to-yellow-800",
      benefits: [
        "Real-time scan tracking",
        "Engagement metrics",
        "Performance insights",
        "Growth analytics"
      ]
    },
    {
      icon: FaQrcode,
      title: "Multi-Link Profile",
      description: "Share multiple links at once - website, social media, portfolio, and more in one place.",
      color: "from-pink-600 to-pink-800",
      benefits: [
        "Unlimited links",
        "Social media integration",
        "Custom branding",
        "Portfolio showcase"
      ]
    },
    {
      icon: FiZap,
      title: "Eco-Friendly",
      description: "Reduce waste with one reusable card that lasts forever. Better for you and the planet.",
      color: "from-green-600 to-green-800",
      benefits: [
        "Zero paper waste",
        "Sustainable materials",
        "Long-lasting durability",
        "Carbon footprint reduction"
      ]
    }
  ];

  const additionalFeatures = [
    {
      icon: FaBolt,
      title: "Lightning Fast",
      description: "Instant loading and sharing with optimized performance"
    },
    {
      icon: FaGlobe,
      title: "Global Access",
      description: "Access and update your card from anywhere in the world"
    },
    {
      icon: FaLock,
      title: "Password Protected",
      description: "Secure your card with a unique 6-digit code"
    },
    {
      icon: FaClock,
      title: "24/7 Availability",
      description: "Your digital card is always accessible, anytime"
    },
    {
      icon: FaRocket,
      title: "Quick Setup",
      description: "Get started in minutes with our simple setup process"
    },
    {
      icon: FaHeart,
      title: "Premium Support",
      description: "Dedicated customer support to help you succeed"
    },
    {
      icon: FaStar,
      title: "Professional Design",
      description: "Beautiful, modern card designs that make an impression"
    },
    {
      icon: FaLink,
      title: "Direct Links",
      description: "Redirect to any website or landing page instantly"
    },
    {
      icon: FaLayerGroup,
      title: "Smart Profiles",
      description: "Create multi-link landing pages with your branding"
    },
    {
      icon: FaWifi,
      title: "NFC Compatible",
      description: "Works with both QR codes and NFC technology"
    },
    {
      icon: FaCloud,
      title: "Cloud Synced",
      description: "All your data is safely stored and synced in the cloud"
    },
    {
      icon: FaDatabase,
      title: "Data Backup",
      description: "Automatic backups ensure you never lose your information"
    },
    {
      icon: FaPalette,
      title: "Customizable",
      description: "Personalize your digital profile with logos and colors"
    },
    {
      icon: FaChartBar,
      title: "Detailed Reports",
      description: "Get comprehensive reports on your card performance"
    },
    {
      icon: FaUserShield,
      title: "Privacy Controls",
      description: "Choose exactly what information you want to share"
    },
    {
      icon: FaUsers,
      title: "Team Ready",
      description: "Perfect for teams and organizations of any size"
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-red-600 selection:text-white">
      {/* Navbar */}
      <nav className="border-b border-white/10 bg-black/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center transform -rotate-6 shadow-xl shadow-red-900/40 hover:rotate-0 transition-transform duration-300">
              <FaQrcode className="text-white text-2xl" />
            </div>
            <span className="font-bold text-2xl tracking-tight">
              Connect<span className="text-red-500">Kard</span>
            </span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-10 text-sm font-semibold text-zinc-400">
            <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
            <Link to="/features" className="text-white transition-colors duration-300">Features</Link>
            <Link to="/how-it-works" className="hover:text-white transition-colors duration-300">How It Works</Link>
            <a href="/#contact" className="hover:text-white transition-colors duration-300">Contact</a>
          </div>

          <div className="flex items-center gap-3">
            {/* Desktop Edit Card Button */}
            <Link to="/editqr" className="hidden sm:block">
              <Button variant="outline" className="px-6 py-2.5 text-sm bg-black/50 border-white/20 hover:bg-white/10">
                <SafeIcon icon={FaEdit} />
                Edit Card
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-white hover:text-red-500 transition-colors"
            >
              <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="text-2xl" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-md border-t border-white/10 animate-in slide-in-from-top duration-300">
            <div className="px-4 py-6 space-y-4">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block text-zinc-400 hover:text-white font-semibold py-2 transition-colors">
                Home
              </Link>
              <Link to="/features" onClick={() => setMobileMenuOpen(false)} className="block text-white font-semibold py-2 transition-colors">
                Features
              </Link>
              <Link to="/how-it-works" onClick={() => setMobileMenuOpen(false)} className="block text-zinc-400 hover:text-white font-semibold py-2 transition-colors">
                How It Works
              </Link>
              <a href="/#contact" onClick={() => setMobileMenuOpen(false)} className="block text-zinc-400 hover:text-white font-semibold py-2 transition-colors">
                Contact
              </a>
              <Link to="/editqr" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full py-3 text-sm bg-black/50 border-white/20 hover:bg-white/10">
                  <SafeIcon icon={FaEdit} />
                  Edit Card
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-950/20 via-black to-black" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center space-y-6 mb-20 animate-in fade-in slide-in-from-bottom duration-700">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600/20 border border-red-500/30 rounded-full text-red-400 text-sm font-bold backdrop-blur-md">
              <SafeIcon icon={FaCheckCircle} />
              <span>Comprehensive Feature Set</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight">
              Everything You Need in <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-400">One Card</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
              ConnectKard is packed with powerful features designed to revolutionize how you network and share your professional information.
            </p>
          </div>

          {/* Main Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {mainFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-8 hover:border-red-500/50 transition-all duration-500 group backdrop-blur-sm hover:bg-zinc-900 hover:-translate-y-2 animate-in fade-in slide-in-from-bottom"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`w-20 h-20 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mb-6 shadow-2xl transform -rotate-6 group-hover:rotate-0 transition-all duration-300`}>
                  <SafeIcon icon={feature.icon} className="text-4xl text-white" />
                </div>
                
                <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                <p className="text-zinc-400 leading-relaxed mb-6">{feature.description}</p>
                
                <div className="space-y-2">
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-zinc-500">
                      <SafeIcon icon={FaCheckCircle} className="text-green-500 text-xs flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Additional Features Section */}
          <div className="mb-16 text-center">
            <h2 className="text-4xl md:text-5xl font-black mb-4">And Much More...</h2>
            <p className="text-zinc-400 text-xl max-w-2xl mx-auto">
              Discover all the additional features that make ConnectKard the ultimate networking solution
            </p>
          </div>

          {/* Additional Features Grid */}
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {additionalFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-zinc-900/30 border border-zinc-800 rounded-2xl p-6 hover:border-red-500/30 transition-all duration-300 group hover:-translate-y-1 animate-in fade-in slide-in-from-bottom"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="w-12 h-12 bg-red-600/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-red-600 transition-all duration-300">
                  <SafeIcon icon={feature.icon} className="text-2xl text-red-500 group-hover:text-white transition-colors duration-300" />
                </div>
                <h4 className="font-bold mb-2">{feature.title}</h4>
                <p className="text-zinc-500 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-red-950/30 via-black to-zinc-950 relative overflow-hidden border-t border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-900/20 via-transparent to-transparent animate-pulse" />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom duration-700">
            <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mx-auto shadow-2xl shadow-red-900/40 transform -rotate-6">
              <SafeIcon icon={FaRocket} className="text-4xl text-white" />
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black">
              Ready to Experience All These Features?
            </h2>
            
            <p className="text-xl md:text-2xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
              Get your ConnectKard today and unlock the future of professional networking!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center pt-4">
              <a 
                href="https://wa.me/917796983999?text=I%20want%20to%20buy%20ConnectKard" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
              >
                <Button className="w-full sm:w-auto px-12 py-6 text-xl font-bold shadow-2xl shadow-red-900/50 hover:shadow-red-900/70 transform hover:scale-105 transition-all duration-300">
                  <SafeIcon icon={FaQrcode} />
                  Get Started Now
                  <SafeIcon icon={FiArrowRight} />
                </Button>
              </a>
              
              <Link to="/how-it-works" className="w-full sm:w-auto">
                <Button variant="outline" className="w-full sm:w-auto px-12 py-6 text-xl font-bold border-2 bg-black/40 backdrop-blur-sm border-zinc-500 hover:bg-white/10 hover:border-white text-white transition-all duration-300">
                  Learn How It Works
                  <SafeIcon icon={FiArrowRight} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800/50 bg-zinc-950 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3 hover:scale-105 transition-transform duration-300">
              <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center transform -rotate-6 hover:rotate-0 transition-transform duration-300">
                <FaQrcode className="text-white text-xl" />
              </div>
              <span className="font-bold text-xl">
                Connect<span className="text-red-500">Kard</span>
              </span>
            </div>
            
            <p className="text-zinc-500 text-sm">
              © 2026 ConnectKard. All rights reserved.
            </p>
            
            <div className="flex gap-8 text-sm text-zinc-400 font-medium">
              <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
              <Link to="/features" className="hover:text-white transition-colors duration-300">Features</Link>
              <a href="/#contact" className="hover:text-white transition-colors duration-300">Contact</a>
              <Link to="/admin/login" className="hover:text-white transition-colors duration-300">Admin</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Features;